import React, { useState, useEffect, createContext, useContext } from 'react';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);

  const addNotification = (notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      timestamp: new Date(),
      ...notification
    };
    
    setNotifications(prev => [newNotification, ...prev.slice(0, 4)]); // Keep only 5 notifications
    
    // Auto-remove after 5 seconds if not persistent
    if (!notification.persistent) {
      setTimeout(() => {
        removeNotification(id);
      }, 5000);
    }
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  return (
    <NotificationContext.Provider value={{ 
      notifications, 
      addNotification, 
      removeNotification, 
      clearAll 
    }}>
      {children}
      <NotificationContainer />
    </NotificationContext.Provider>
  );
}

function NotificationContainer() {
  const { notifications, removeNotification } = useNotifications();

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {notifications.map((notification) => (
        <NotificationItem 
          key={notification.id} 
          notification={notification} 
          onRemove={() => removeNotification(notification.id)}
        />
      ))}
    </div>
  );
}

function NotificationItem({ notification, onRemove }) {
  const getIcon = (type) => {
    switch (type) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'info': return 'ℹ️';
      case 'booking': return '📅';
      case 'message': return '💬';
      case 'payment': return '💰';
      default: return 'ℹ️';
    }
  };

  const getColorClasses = (type) => {
    switch (type) {
      case 'success': return 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400';
      case 'error': return 'bg-red-500/10 border-red-500/30 text-red-400';
      case 'warning': return 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400';
      case 'info': return 'bg-blue-500/10 border-blue-500/30 text-blue-400';
      case 'booking': return 'bg-purple-500/10 border-purple-500/30 text-purple-400';
      case 'message': return 'bg-klorah-primary/10 border-klorah-primary/30 text-klorah-primary';
      case 'payment': return 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400';
      default: return 'bg-klorah-card/80 border-klorah-border text-klorah-text';
    }
  };

  return (
    <div className={`
      max-w-sm w-full backdrop-blur-lg border rounded-xl shadow-lg p-4 
      ${getColorClasses(notification.type)}
    `}>
      <div className="flex items-start">
        <div className="flex-shrink-0 text-lg mr-3">
          {getIcon(notification.type)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium">{notification.title}</p>
              {notification.message && (
                <p className="text-xs opacity-80 mt-1">{notification.message}</p>
              )}
              <p className="text-xs opacity-60 mt-2">
                {notification.timestamp.toLocaleTimeString()}
              </p>
            </div>
            <button
              onClick={onRemove}
              className="ml-2 text-klorah-text-light hover:text-white focus:outline-none transition-colors"
            >
              <span className="sr-only">Close</span>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          {notification.action && (
            <button
              onClick={notification.action.onClick}
              className="mt-2 text-xs font-medium underline hover:no-underline"
            >
              {notification.action.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Hook for common notification patterns
export const useNotificationHelpers = () => {
  const { addNotification } = useNotifications();

  return {
    notifySuccess: (title, message) => addNotification({
      type: 'success',
      title,
      message
    }),
    
    notifyError: (title, message) => addNotification({
      type: 'error',
      title,
      message
    }),
    
    notifyBooking: (participantName, workshopTitle) => addNotification({
      type: 'booking',
      title: 'New Booking!',
      message: `${participantName} booked ${workshopTitle}`,
      action: {
        label: 'View Details',
        onClick: () => console.log('Navigate to booking details')
      }
    }),
    
    notifyMessage: (senderName, preview) => addNotification({
      type: 'message',
      title: `Message from ${senderName}`,
      message: preview,
      action: {
        label: 'Reply',
        onClick: () => console.log('Open messages')
      }
    }),
    
    notifyPayment: (amount, participantName) => addNotification({
      type: 'payment',
      title: 'Payment Received!',
      message: `$${amount} from ${participantName}`,
      action: {
        label: 'View Transaction',
        onClick: () => console.log('Open transaction details')
      }
    })
  };
};

export default NotificationProvider; 